import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useCallback, useEffect, useState } from 'react';
import i18next from 'i18next';
import { useFormContext } from 'react-hook-form';
import { Box } from '@famly/mf_layout_box';
import { Stack } from '@famly/mf_layout_stack';
import { Text } from '@famly/mf_data-display_text';
import { Button } from '@famly/mf_input_button';
import { Icon } from '@famly/mf_data-display_icon';
import { FormRow } from '@famly/stat_forms_form-row';
import { fetchLocation, LocationPermissionDeniedError } from './fetch-location';
import { kinderConnectGeolocationCapturedStatusE2eId, kinderConnectGeolocationRetryButtonE2eId, } from './kinder-connect-capture-e2e-ids';
export const CustomGeolocationCaptureRow = ({ isDisabled }) => {
    const { setValue } = useFormContext();
    const [state, setState] = useState({ status: 'loading' });
    // Async-only: fetches the position and updates state from its .then()/.catch(), never
    // synchronously — safe to call directly from the mount effect below without tripping
    // react-hooks/set-state-in-effect.
    const beginFetchingLocation = useCallback(() => {
        fetchLocation()
            .then(position => {
            setValue('latitude', position.coords.latitude.toString(), { shouldValidate: true });
            setValue('longitude', position.coords.longitude.toString(), { shouldValidate: true });
            setState({ status: 'captured' });
        })
            .catch(error => {
            setState({
                status: 'error',
                reason: error instanceof LocationPermissionDeniedError ? 'permission-denied' : 'unavailable',
            });
        });
    }, [setValue]);
    // Same as beginFetchingLocation, but also flips the status to 'loading' up front —
    // for use from the retry button, where state is not already 'loading'.
    const requestLocation = useCallback(() => {
        setState({ status: 'loading' });
        beginFetchingLocation();
    }, [beginFetchingLocation]);
    useEffect(() => {
        if (isDisabled) {
            return;
        }
        beginFetchingLocation();
    }, [isDisabled, beginFetchingLocation]);
    return (_jsx(FormRow, { children: _jsxs(Box, { paddingY: 1, children: [_jsx(Text, { variant: "body-small", emphasized: true, children: i18next.t($ => $.stat.kinderConnectCapture.businessLogic.src.translations.json.geolocationCapture.label) }), _jsxs(Box, { marginTop: 1, children: [state.status === 'loading' && (_jsx(Stack, { direction: "row", gap: 2, alignItems: "center", children: _jsx(Text, { variant: "body-small", color: "n300", children: i18next.t($ => $.stat.kinderConnectCapture.businessLogic.src.translations.json
                                    .geolocationCapture.fetching) }) })), state.status === 'captured' && (_jsxs(Stack, { direction: "row", gap: 1, alignItems: "center", "data-e2e-id": kinderConnectGeolocationCapturedStatusE2eId, children: [_jsx(Icon, { name: "check_circle", filled: true, color: "g400", size: 20 }), _jsx(Text, { variant: "body-small", color: "n300", children: i18next.t($ => $.stat.kinderConnectCapture.businessLogic.src.translations.json
                                        .geolocationCapture.captured) })] })), state.status === 'error' && (_jsxs(Stack, { gap: 2, children: [_jsxs(Stack, { direction: "row", gap: 2, alignItems: "flex-start", children: [_jsx(Icon, { name: "location_off", color: "r400", size: 20 }), _jsx(Text, { variant: "body-small", color: "r400", children: state.reason === 'permission-denied'
                                                ? i18next.t($ => $.stat.kinderConnectCapture.businessLogic.src.translations.json
                                                    .geolocationCapture.permissionDenied)
                                                : i18next.t($ => $.stat.kinderConnectCapture.businessLogic.src.translations.json
                                                    .geolocationCapture.unavailable) })] }), _jsx(Button, { variant: "transparent", size: "compact", icon: "refresh", isDisabled: isDisabled, "data-e2e-id": kinderConnectGeolocationRetryButtonE2eId, "aria-label": i18next.t($ => $.stat.kinderConnectCapture.businessLogic.src.translations.json
                                        .geolocationCapture.retryAriaLabel), onClick: requestLocation })] }))] })] }) }));
};
